import PropTypes from "prop-types";
import * as React from "react";
import { useHistory, useLocation } from "react-router-dom";
import { Influencer, InfluencerEvent } from "../../../domain";
import { useCurrentUser } from "../../../globalState";
import {
  useInfluencer,
  useInfluencerEvents,
  useMediaQuery,
} from "../../../hooks";
import { useNotification } from "../../../services/Notifications";
import {
  Accordion,
  AddIcon,
  Button,
  Card,
  FAB,
  SectionHead,
  Select,
  Spinner,
  Typography,
} from "../../../themeV2";
import CompletionDeadlineField from "./CompletionDeadlineField";
import css from "./EventsSection.module.css";
import { nextBatchSummary, toEventEntry } from "./eventSummary";
import MiniEvent from "./MiniEvent";
import { dashboardPath } from "../../../domain/dashboardPaths";

const OFF_CAMERA_PANEL = "off-camera";

const fulfillmentOptions = [
  { value: "hidden", label: "Hidden" },
  { value: "fast", label: "Fast" },
  { value: "moderate", label: "Moderate" },
  { value: "slow", label: "Slow" },
];

const EventsSection = React.memo(function EventsSection({ route }) {
  const currentUser = useCurrentUser();
  const notify = useNotification();
  const history = useHistory();
  const location = useLocation();

  const { influencer, refresh } = useInfluencer({
    route,
    forShopBuilder: true,
  });

  const { influencerEvents } = useInfluencerEvents(influencer?.influencerId);

  const isCompact = useMediaQuery("(max-width: 600px)");

  /* Off-camera fulfillment is a rarely-touched panel that costs a screenful on a
     phone, so below the width where the two cards stack it starts collapsed with
     only the next batch date showing. */
  const isStacked = useMediaQuery("(max-width: 768px)");
  const [openPanels, setOpenPanels] = React.useState(() =>
    isStacked ? [] : [OFF_CAMERA_PANEL],
  );

  React.useEffect(() => {
    setOpenPanels(isStacked ? [] : [OFF_CAMERA_PANEL]);
  }, [isStacked]);

  const [fulfillmentSpeed, setFulfillmentSpeed] = React.useState(
    influencer?.fulfillmentSpeed,
  );
  const [isUpdating, setIsUpdating] = React.useState(false);

  const handleFulfillmentChange = (newValue) => {
    const previousValue = fulfillmentSpeed;
    setFulfillmentSpeed(newValue);
    setIsUpdating(true);

    const updates = { fulfillment_speed: newValue };

    Influencer.update({ influencer, updates })
      .then(() => refresh())
      .catch(() => {
        setFulfillmentSpeed(previousValue);
        notify({ message: "Failed to update fulfillment speed" });
      })
      .finally(() => setIsUpdating(false));
  };

  React.useEffect(() => {
    setFulfillmentSpeed(influencer?.fulfillmentSpeed);
  }, [influencer?.fulfillmentSpeed]);

  const handleAddEvent = React.useCallback(() => {
    if (!influencer) return;
    history.push(
      dashboardPath({ shop: influencer.route, page: "events/new" }),
      { returnTo: location.pathname },
    );
  }, [history, influencer, location.pathname]);

  // Detect return from EventFormPage
  React.useEffect(() => {
    if (location.state?.eventUpdated) {
      refresh();
      history.replace(location.pathname, {
        ...location.state,
        eventUpdated: undefined,
      });
    }
  }, [location.state, location.pathname, history, refresh]);

  const { liveEvents, offCameraEvents } = React.useMemo(() => {
    if (!influencerEvents?.length) {
      return { liveEvents: [], offCameraEvents: [] };
    }

    return {
      liveEvents: influencerEvents.filter(
        (evt) => !InfluencerEvent.isNonLiveSigning(evt),
      ),
      offCameraEvents: influencerEvents.filter(
        InfluencerEvent.isNonLiveSigning,
      ),
    };
  }, [influencerEvents]);

  const nextBatch = offCameraEvents[0];
  const nextBatchEntry = nextBatch && toEventEntry(nextBatch.raw());

  return (
    <>
      <SectionHead title="Events & scheduling">
        {Boolean(influencer) && (
          <FAB
            variant={isCompact ? "standard" : "extended"}
            size="md"
            icon={<AddIcon />}
            label="Add event"
            onClick={handleAddEvent}
          />
        )}
      </SectionHead>

      {!influencer && (
        <div className={css.spinnerWrap}>
          <Spinner />
        </div>
      )}

      {Boolean(influencer) && (
        <div className={css.grid}>
          {/* Left — Upcoming events */}
          <Card.Root>
            <Card.Content>
              <Typography
                variant="label-lg"
                color="variant"
                weight="bold"
                as="p"
                className={css.subHead}
              >
                Upcoming events
              </Typography>
              {liveEvents.length === 0 && (
                <Typography
                  variant="body-lg"
                  color="variant"
                  as="p"
                  className={css.emptyText}
                >
                  No upcoming live events scheduled
                </Typography>
              )}
              <div className={css.eventList}>
                {liveEvents.slice(0, 3).map((evt) => {
                  const { month, day, title, subtitle } = toEventEntry(
                    evt.raw(),
                  );
                  return (
                    <MiniEvent
                      key={evt.influencerEventId}
                      month={month}
                      day={day}
                      title={title}
                      subtitle={subtitle}
                      onClick={() =>
                        history.push(
                          dashboardPath({
                            shop: influencer.route,
                            page: `events/${evt.influencerEventId}/edit`,
                          }),
                          { returnTo: location.pathname },
                        )
                      }
                    />
                  );
                })}
              </div>
              {liveEvents.length > 3 && (
                <div className={css.viewMore}>
                  <Button
                    variant="text"
                    size="sm"
                    onClick={() =>
                      history.push(
                        dashboardPath({
                          shop: influencer.route,
                          page: "events",
                        }),
                        {
                          returnTo: location.pathname,
                        },
                      )
                    }
                  >
                    View all {liveEvents.length} events
                  </Button>
                </div>
              )}
            </Card.Content>
          </Card.Root>

          <Card.Root>
            <Accordion.Root value={openPanels} onValueChange={setOpenPanels}>
              <Accordion.Item
                value={OFF_CAMERA_PANEL}
                className={css.offCameraItem}
              >
                <Accordion.Header>
                  <Accordion.Trigger className={css.offCameraTrigger}>
                    <Typography
                      variant="label-lg"
                      color="variant"
                      weight="bold"
                      as="span"
                      className={css.offCameraLabel}
                    >
                      Off-camera fulfillment
                    </Typography>
                    <span className={css.offCameraSummary}>
                      {nextBatchSummary(nextBatch?.raw())}
                    </span>
                  </Accordion.Trigger>
                </Accordion.Header>

                <Accordion.Panel className={css.offCameraPanel}>
                  {currentUser.isAdmin && (
                    <div className={css.fulfillmentRow}>
                      <Select.Root
                        aria-label="Set Fulfillment Speed"
                        value={
                          fulfillmentOptions.find(
                            (opt) => opt.value === fulfillmentSpeed,
                          )?.label
                        }
                        onValueChange={handleFulfillmentChange}
                        disabled={isUpdating}
                      >
                        {fulfillmentOptions.map((opt) => (
                          <Select.Item
                            key={opt.value}
                            value={opt.value}
                            label={opt.label}
                          >
                            {opt.label}
                          </Select.Item>
                        ))}
                      </Select.Root>
                      {isUpdating && <Spinner size="small" />}

                      <CompletionDeadlineField
                        influencer={influencer}
                        refresh={refresh}
                      />
                    </div>
                  )}

                  {nextBatch ? (
                    <div
                      className={
                        currentUser.isAdmin ? css.nextBatchSection : undefined
                      }
                    >
                      {currentUser.isAdmin ? (
                        <Typography
                          variant="label-lg"
                          color="variant"
                          weight="bold"
                          as="p"
                          className={css.subHead}
                        >
                          Next batch
                        </Typography>
                      ) : null}
                      <MiniEvent
                        month={nextBatchEntry.month}
                        day={nextBatchEntry.day}
                        title={nextBatchEntry.title}
                        subtitle={nextBatchEntry.subtitle}
                        onClick={
                          currentUser.isAdmin
                            ? () =>
                                history.push(
                                  dashboardPath({
                                    shop: influencer.route,
                                    page: `events/${nextBatch.influencerEventId}/edit`,
                                  }),
                                  { returnTo: location.pathname },
                                )
                            : undefined
                        }
                      />
                    </div>
                  ) : (
                    <Typography
                      variant="body-lg"
                      color="variant"
                      as="p"
                      className={css.emptyText}
                    >
                      No off-camera batches scheduled
                    </Typography>
                  )}
                </Accordion.Panel>
              </Accordion.Item>
            </Accordion.Root>
          </Card.Root>
        </div>
      )}
    </>
  );
});

EventsSection.propTypes = {
  route: PropTypes.string.isRequired,
};

export default EventsSection;
